<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import KanbanApi from 'dashboard/api/kanbanConnections';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

// ─── Estado da conexão ────────────────────────────────────────────────────────
const instance = ref(null);
const qrcode = ref(null);
const isLoading = ref(true);
const isRefreshing = ref(false);
const isDisconnecting = ref(false);
let pollTimer = null;

const status = computed(() => instance.value?.status || 'disconnected');
const provider = computed(() => instance.value?.provider || '');

const statusLabel = computed(() => {
  switch (status.value) {
    case 'connected':  return 'Conectado';
    case 'connecting': return 'Conectando...';
    case 'qr_code':    return 'Aguardando QR Code';
    case 'error':      return 'Erro na conexão';
    default:           return 'Desconectado';
  }
});

const statusDot = computed(() => ({
  'bg-green-500':  status.value === 'connected',
  'bg-yellow-400': status.value === 'connecting',
  'bg-red-500':    status.value === 'error',
  'bg-gray-400':   ['disconnected', 'qr_code'].includes(status.value),
}));

const isConnected = computed(() => status.value === 'connected');
const needsQr = computed(() => ['disconnected', 'connecting', 'qr_code', 'error'].includes(status.value));

// ─── Configurações da instância ───────────────────────────────────────────────
const config = ref({
  reject_call:     false,
  msg_call:        '',
  groups_ignore:   false,
  always_online:   false,
  read_messages:   false,
  read_status:     false,
  show_agent_name: true,
});
const isLoadingConfig = ref(false);
const isSavingConfig = ref(false);
const configDirty = ref(false);

// Options visíveis por provider
const configOptions = computed(() => {
  const all = [
    { key: 'reject_call',   label: 'Rejeitar ligações',       type: 'toggle', providers: ['evolution'] },
    { key: 'msg_call',      label: 'Mensagem ao rejeitar',    type: 'text',   providers: ['evolution'],
      placeholder: 'Ex: Não aceitamos ligações via WhatsApp', condition: 'reject_call' },
    { key: 'groups_ignore', label: 'Ignorar mensagens de grupos', type: 'toggle', providers: ['evolution', 'waha'] },
    { key: 'always_online', label: 'Sempre online (presença)', type: 'toggle', providers: ['evolution', 'waha'] },
    { key: 'read_messages', label: 'Marcar mensagens como lidas', type: 'toggle', providers: ['evolution', 'waha'] },
    { key: 'read_status',   label: 'Marcar status como lidos',    type: 'toggle', providers: ['evolution'] },
    { key: 'show_agent_name', label: 'Mostrar nome do agente na mensagem', type: 'toggle', providers: ['evolution', 'waha'] },
  ];
  return all.filter(o => !o.providers || o.providers.includes(provider.value));
});

async function loadConfig() {
  if (!instance.value) return;
  isLoadingConfig.value = true;
  try {
    const resp = await KanbanApi.getConfig(accountId.value, instance.value.id);
    if (resp.data.config) {
      config.value = { ...config.value, ...resp.data.config };
    }
  } finally {
    isLoadingConfig.value = false;
    configDirty.value = false;
  }
}

async function saveConfig() {
  if (!instance.value) return;
  isSavingConfig.value = true;
  try {
    await KanbanApi.updateConfig(accountId.value, instance.value.id, config.value);
    useAlert('Configurações salvas com sucesso.', 'success');
    configDirty.value = false;
  } catch {
    useAlert('Erro ao salvar configurações.');
  } finally {
    isSavingConfig.value = false;
  }
}

function markDirty() {
  configDirty.value = true;
}

// ─── QR / Estado ─────────────────────────────────────────────────────────────
async function load() {
  isLoading.value = true;
  try {
    const resp = await KanbanApi.getByInbox(accountId.value, props.inbox.id);
    instance.value = resp.data.data;
    if (instance.value) {
      if (needsQr.value) await fetchQrcode();
      await loadConfig();
    }
  } catch {
    // sem conexão vinculada
  } finally {
    isLoading.value = false;
  }
}

async function fetchQrcode() {
  if (!instance.value) return;
  try {
    const resp = await KanbanApi.getQrcode(accountId.value, instance.value.id);
    if (resp.data.qrcode) qrcode.value = resp.data.qrcode;
  } catch {
    // silencioso
  }
}

async function refreshState() {
  if (!instance.value) return;
  isRefreshing.value = true;
  try {
    const resp = await KanbanApi.getState(accountId.value, instance.value.id);
    const wasConnected = isConnected.value;
    instance.value = { ...instance.value, status: resp.data.status };
    if (needsQr.value) {
      await fetchQrcode();
    } else {
      qrcode.value = null;
      if (!wasConnected) await loadConfig(); // carrega config ao conectar
    }
  } catch {
    useAlert('Erro ao verificar estado da conexão.');
  } finally {
    isRefreshing.value = false;
  }
}

async function disconnect() {
  if (!instance.value) return;
  if (!window.confirm('Desconectar este número? A sessão WhatsApp será encerrada.')) return;
  isDisconnecting.value = true;
  try {
    await KanbanApi.destroy(accountId.value, instance.value.id);
    instance.value = null;
    qrcode.value = null;
    useAlert('Conexão removida com sucesso.', 'success');
  } catch {
    useAlert('Erro ao desconectar.');
  } finally {
    isDisconnecting.value = false;
  }
}

function startPolling() {
  pollTimer = setInterval(async () => {
    if (!isConnected.value && instance.value) await refreshState();
  }, 8000);
}

onMounted(async () => {
  await load();
  startPolling();
});

onUnmounted(() => clearInterval(pollTimer));
</script>

<template>
  <div class="p-6 max-w-xl space-y-6">
    <!-- ── Título ── -->
    <h3 class="text-base font-semibold text-gray-800 dark:text-gray-100">
      Conexão WhatsApp
    </h3>

    <!-- Loading inicial -->
    <div v-if="isLoading" class="text-sm text-gray-400">Carregando...</div>

    <!-- Sem instância -->
    <div v-else-if="!instance" class="text-sm text-gray-500">
      <p>Esta caixa ainda não está vinculada a um provedor WhatsApp.</p>
      <p class="mt-1 text-xs text-gray-400">
        Para vincular, crie uma nova caixa usando "WhatsApp (Evolution)" ou "WhatsApp (WAHA)".
      </p>
    </div>

    <!-- Com instância -->
    <template v-else>

      <!-- ── Status badge ── -->
      <div class="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <span class="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" :class="statusDot" />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ statusLabel }}</p>
          <p class="text-xs text-gray-400 truncate">
            {{ instance.instance_name }}
            <span v-if="provider" class="ml-1 capitalize">({{ provider }})</span>
          </p>
        </div>
        <button
          class="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
          :disabled="isRefreshing"
          @click="refreshState"
        >
          {{ isRefreshing ? 'Verificando...' : 'Atualizar' }}
        </button>
      </div>

      <!-- ── QR Code ── -->
      <div v-if="needsQr" class="space-y-3">
        <p class="text-sm text-gray-600 dark:text-gray-300 font-medium">
          Escaneie o QR Code com o WhatsApp para conectar:
        </p>
        <div v-if="qrcode" class="flex flex-col items-start gap-2">
          <img
            :src="qrcode.startsWith('data:') ? qrcode : `data:image/png;base64,${qrcode}`"
            alt="QR Code WhatsApp"
            class="w-44 h-44 border-4 border-white shadow rounded"
          />
          <button
            class="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            :disabled="isRefreshing"
            @click="fetchQrcode"
          >
            Gerar novo QR Code
          </button>
        </div>
        <p v-else class="text-xs text-gray-400 italic">
          QR Code não disponível. Clique em "Atualizar" para tentar novamente.
        </p>
      </div>

      <!-- ── Conectado ── -->
      <div
        v-if="isConnected"
        class="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700"
      >
        <p class="text-sm font-semibold text-green-700 dark:text-green-400">
          WhatsApp conectado!
        </p>
        <p class="text-xs text-green-600 dark:text-green-500 mt-0.5">
          Mensagens serão recebidas automaticamente nesta caixa de entrada.
        </p>
      </div>

      <!-- ── Configurações da instância ── -->
      <div v-if="configOptions.length > 0" class="space-y-4">
        <div class="flex items-center justify-between">
          <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Configurações da instância
          </h4>
          <span v-if="isLoadingConfig" class="text-xs text-gray-400">Carregando...</span>
        </div>

        <div class="divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <template v-for="opt in configOptions" :key="opt.key">
            <!-- Oculta campo de texto se a condição toggle for false -->
            <template v-if="!opt.condition || config[opt.condition]">
              <!-- Toggle -->
              <div
                v-if="opt.type === 'toggle'"
                class="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800"
              >
                <label class="text-sm text-gray-700 dark:text-gray-200 cursor-pointer select-none" :for="`cfg-${opt.key}`">
                  {{ opt.label }}
                </label>
                <button
                  :id="`cfg-${opt.key}`"
                  type="button"
                  role="switch"
                  :aria-checked="config[opt.key]"
                  class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
                  :class="config[opt.key] ? 'bg-yellow-500' : 'bg-gray-300 dark:bg-gray-600'"
                  @click="config[opt.key] = !config[opt.key]; markDirty()"
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform"
                    :class="config[opt.key] ? 'translate-x-4' : 'translate-x-1'"
                  />
                </button>
              </div>

              <!-- Text -->
              <div
                v-else-if="opt.type === 'text'"
                class="flex flex-col gap-1 px-4 py-3 bg-white dark:bg-gray-800"
              >
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ opt.label }}</label>
                <input
                  v-model="config[opt.key]"
                  type="text"
                  :placeholder="opt.placeholder"
                  class="text-sm border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-200 outline-none focus:border-yellow-400"
                  @input="markDirty"
                />
              </div>
            </template>
          </template>
        </div>

        <!-- Botão Salvar -->
        <div class="flex justify-end">
          <button
            class="px-4 py-1.5 text-sm font-medium rounded transition-colors"
            :class="configDirty
              ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-default'"
            :disabled="!configDirty || isSavingConfig"
            @click="saveConfig"
          >
            {{ isSavingConfig ? 'Salvando...' : 'Salvar configurações' }}
          </button>
        </div>
      </div>

      <!-- ── Zona de perigo ── -->
      <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
        <p class="text-xs text-gray-400 mb-2">Zona de perigo</p>
        <button
          class="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
          :disabled="isDisconnecting"
          @click="disconnect"
        >
          {{ isDisconnecting ? 'Removendo conexão...' : 'Remover conexão' }}
        </button>
      </div>

    </template>
  </div>
</template>
